import classnames from 'classnames';
import { useFormatter, useTranslations } from 'next-intl';
import React, { useEffect, useRef } from 'react';

import { IconBack16 } from '../icons/ic-back';
import { IconForth16 } from '../icons/ic-forth';

import {
    PageSlot,
    SLOT_MAX_MOBILE,
    buildDependencyKey,
    buildPageRange,
    buildPageSlots
} from './table-paginator-helper';

type TablePaginatorBaseProps = {
    className?: string;
    page: number;
    setPage: (page: number) => void;
    /** `undefined` until the first response lands; treated as "no pages known yet". */
    pageCount: number | undefined;
    /** Marks the target page as pending while the fetch for it is in flight. */
    isFetching?: boolean;
    resetPageDependencies?: unknown[];
};

export type TablePaginatorProps = TablePaginatorBaseProps &
    (
        | { showRange?: true; perPage: number; totalItems: number | undefined }
        | { showRange: false; perPage?: never; totalItems?: never }
    );

// 32px wide at every breakpoint -- that is what makes the block's width, and with it the arrows'
// position, depend on pageCount alone. Below lg the button grows taller for the thumb while its
// marker stays a 32x32 circle, so the active page is never drawn as an oval.
const SLOT_CLASS_NAME = 'group flex h-40 w-32 lg:h-32 shrink-0 items-center justify-center outline-none';

const SLOT_MARKER_CLASS_NAME =
    'flex h-32 w-32 items-center justify-center rounded-16 text-body-bold-14 tabular-nums ' +
    'group-focus-visible:ring-2 group-focus-visible:ring-cyan';

// The arrows are the only controls below lg, where a thumb -- not a mouse -- has to hit them.
const ARROW_CLASS_NAME =
    'flex h-40 w-40 lg:h-32 lg:w-32 shrink-0 items-center justify-center rounded-16 ' +
    'outline-none focus-visible:ring-2 focus-visible:ring-cyan disabled:opacity-50';

function TablePaginator(props: TablePaginatorProps): React.JSX.Element | null {
    const { className, page, setPage, pageCount: pageCountProp, isFetching, resetPageDependencies } = props;
    const pageCount = pageCountProp ?? 0;

    const t = useTranslations();
    const formatter = useFormatter();

    // Filters, sorting and search send the table back to page 1 -- but not on mount, where the page
    // may legitimately come from the URL and where the unconditional reset used to re-enter this
    // effect on every dependency identity change.
    const dependencyKey = buildDependencyKey(resetPageDependencies ?? []);
    const lastDependencyKey = useRef(dependencyKey);
    useEffect(() => {
        if (lastDependencyKey.current === dependencyKey) {
            return;
        }
        lastDependencyKey.current = dependencyKey;
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dependencyKey]);

    // A shrinking result set (last row of the last page deleted, filter narrowed) leaves `page` past
    // the end, which otherwise surfaces as a range label reading "41 - 40 von 40".
    useEffect(() => {
        if (pageCount > 0 && page > pageCount) {
            setPage(pageCount);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageCount]);

    const showRange = props.showRange !== false;
    const slots = buildPageSlots(page, pageCount);
    const mobileSlots = buildPageSlots(page, pageCount, SLOT_MAX_MOBILE);

    // The table footer sits on the dark table surface; the compact variant is embedded in light
    // panels (search dropdowns, pickers) and therefore inherits its colours from the host.
    const onDarkSurface = showRange;
    const hoverClassName = onDarkSurface ? 'group-hover:bg-white/10' : 'group-hover:bg-darkBlue/10';
    const arrowHoverClassName = onDarkSurface ? 'enabled:hover:bg-white/10' : 'enabled:hover:bg-darkBlue/10';
    const pageRange = showRange ? buildPageRange(page, props.perPage, props.totalItems ?? 0, pageCount) : null;

    if (!showRange && !(pageCount > 1)) {
        return null;
    }

    function renderSlot(slot: PageSlot, index: number): React.JSX.Element {
        if (slot === 'ellipsis-start' || slot === 'ellipsis-end') {
            return (
                <span
                    key={`${slot}-${index}`}
                    aria-hidden
                    className={classnames(SLOT_CLASS_NAME, 'text-body-bold-14 opacity-50')}>
                    …
                </span>
            );
        }

        const isCurrent = slot === page;
        return (
            <button
                key={slot}
                type="button"
                aria-current={isCurrent ? 'page' : undefined}
                aria-label={t(isCurrent ? 'list.pagination.current-page' : 'list.pagination.page-number', {
                    0: slot.toString()
                })}
                className={SLOT_CLASS_NAME}
                onClick={() => setPage(slot)}>
                <span
                    className={classnames(
                        SLOT_MARKER_CLASS_NAME,
                        isCurrent ? classnames('bg-cyan/20', onDarkSurface && 'text-cyan') : hoverClassName,
                        isCurrent && isFetching && 'animate-pulse'
                    )}>
                    {slot}
                </span>
            </button>
        );
    }

    return (
        <div
            className={classnames(
                className,
                'grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] items-center gap-y-8 py-12 px-16 ' +
                    'lg:py-0 lg:h-56 cursor-default',
                showRange && 'text-white'
            )}>
            <div className="min-w-0 truncate space-x-6 tracking-regular text-center lg:text-left">
                {showRange &&
                    (pageRange ? (
                        <>
                            <span className="text-body-bold-14 tabular-nums">
                                {formatter.number(pageRange.start)}–{formatter.number(pageRange.end)}
                            </span>
                            <span className="text-body-14">{t('list.pagination.from')}</span>
                            <span className="text-body-bold-14 tabular-nums">
                                {t('list.pagination.entries', { 0: formatter.number(props.totalItems ?? 0) })}
                            </span>
                        </>
                    ) : (
                        <span className="text-body-14 text-white/50">{t('list.pagination.no-entries')}</span>
                    ))}
            </div>

            {pageCount > 1 ? (
                <nav
                    aria-label={t('list.pagination.aria-label')}
                    aria-busy={isFetching || undefined}
                    className="flex w-full lg:w-auto items-center justify-between lg:justify-center gap-4">
                    <button
                        type="button"
                        disabled={page <= 1}
                        aria-label={t('list.pagination.previous-page')}
                        className={classnames(ARROW_CLASS_NAME, arrowHoverClassName)}
                        onClick={() => setPage(page - 1)}>
                        <IconBack16 />
                    </button>

                    {/* Two windows, one per breakpoint: seven slots need 248px between the arrows, which
                        a 360px viewport does not have. `hidden` is display:none, so the window that is
                        out of play is also out of the accessibility tree. */}
                    <div className="flex lg:hidden items-center gap-4">{mobileSlots.map(renderSlot)}</div>
                    <div className="hidden lg:flex items-center gap-4">{slots.map(renderSlot)}</div>

                    <button
                        type="button"
                        disabled={page >= pageCount}
                        aria-label={t('list.pagination.next-page')}
                        className={classnames(ARROW_CLASS_NAME, arrowHoverClassName)}
                        onClick={() => setPage(page + 1)}>
                        <IconForth16 />
                    </button>
                </nav>
            ) : (
                <div className="hidden lg:block" />
            )}

            <div className="hidden lg:block min-w-0" />
        </div>
    );
}

export default TablePaginator;
